import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  PartnerType,
  getKeyFromValue,
  updateAppAlert,
  useAccountId,
  useCurrentCloudProvider,
  useUserType,
} from '@labrav/common'
import { Link, useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useState } from 'react'
import PrivateOffers from '../PrivateOffers/PrivateOffers'
import { useDispatch, useSelector } from '../../store'
import {
  OfferStatus,
  OfferTypeRenew,
  PrivateOffer,
  PrivateOffersState,
  offerStatusMap,
} from '../../modules/privateOffer/reducer'
// OfferTypeRenew used by PrivateOffersActionsLabraUI
import {
  clearPrivateOffers,
  getPrivateOffers,
  patchOfferData,
  updateOffer,
} from '../../modules/privateOffer/action'
import clsx from 'clsx'
import { useStyles } from './PrivateOfferWrapperUI.styles'
import {
  ButtonV2,
  ColumnMenuOption,
  DateTime,
  DropdownButtonV2,
  LabraToolTip,
  LabraToolTipV2,
  PlanType,
} from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import { TextField } from '@mui/material'
import { VisibilityOutlined } from '@mui/icons-material'
import { ClassNameMap } from '@mui/styles'
import { CopyToClipboard } from '../../common/components/CopyToClipboard/CopyToClipboard'
import {
  CopiedToClipboardSuccessMessage,
  CopiedToClipboardUnsuccessfulMessage,
} from '../../common/utils/messagesContants'
import LinkIcon from '@mui/icons-material/Link'
import type { DetailedOfferPreviewData } from '../PrivateOffers/types'
import { DetailedOffer } from '../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { ShortenedName } from '../../common/components/ShortenedName/ShortenedName'
import {
  CloudMarketplace,
  FlyoutFunction,
} from '../../modules/productsListing/types'
import { getProducts } from '../../modules/productsListing/action'
import {
  getExpirationDateFromPrivateOfferV1 as getExpirationDateFromPrivateOffer,
  getKeyCloudAccount,
  getOfferLinkPrivateOffer,
  getOfferRenewalType,
  getProductIdFromPrivateOffer,
  getStatusOffer,
} from '../../utils/privateOffersListHelpers'
import { useNavigate } from 'react-router-dom'
import { ProductSelectorDropdown } from '../../common/components/ProductSelectorDropdown/ProductSelectorDropdown'

type PrivateOffersActionsLabraUI = {
  classes: ClassNameMap
  offer: PrivateOffer
  productId: string
  externalBaseURL?: string
  search?: string
  cloudType: CloudMarketplace
}
export const PrivateOffersActionsLabraUI = React.memo(
  ({
    classes,
    offer,
    productId,
    externalBaseURL,
    search,
    cloudType,
  }: PrivateOffersActionsLabraUI) => {
    const offerLink = getOfferLinkPrivateOffer(cloudType, offer)
    const isOfferDraft =
      cloudType === 'AZURE'
        ? offer.offerStatus === OfferStatus.OFFER_IN_DRAFT || !!offer.draft
        : offer.draft

    const isAWSRenewalDraftOffer =
      isOfferDraft &&
      offer?.type === OfferTypeRenew.RENEWAL &&
      !isEmpty(offer?.labraSubscriptionId) &&
      cloudType === 'AWS'

    const awsRenewalDraftRedirectURL = `${
      externalBaseURL?.replace('/product-listing', '') || '/flyout'
    }/subscriptions/${offer?.labraSubscriptionId}/edit/${offer?.privateOfferId}`

    // For external URLs, check if externalBaseURL already contains productId
    const baseURL = externalBaseURL || '/flyout/products-listing'
    const externalURLHasProductId =
      externalBaseURL && !externalBaseURL.endsWith('/product-listing')

    const redirectURL = isAWSRenewalDraftOffer
      ? awsRenewalDraftRedirectURL
      : externalBaseURL
        ? externalURLHasProductId
          ? `${baseURL}${
              isOfferDraft
                ? `/private-offers/${offer.privateOfferId}/edit`
                : `/private-offers/${offer.privateOfferId}/details`
            }`
          : `${baseURL}/${productId}${
              isOfferDraft
                ? `/private-offers/${offer.privateOfferId}/edit`
                : `/private-offers/${offer.privateOfferId}/details`
            }`
        : `${baseURL}/${productId}${
            isOfferDraft
              ? `/private-offers/${offer.privateOfferId}/edit`
              : `/private-offers/${offer.privateOfferId}/details`
          }`
    return (
      <div className={classes.actionsContainer}>
        {!externalBaseURL && (
          <div className={classes.actionIcon}>
            <LabraToolTip
              label={
                isEmpty(offerLink)
                  ? `The Private Offer URL is not yet ready. It is in progress at ${cloudType}.`
                  : 'Click to copy the Private Offer URL. '
              }
              tooltipPlacement="top"
            >
              <CopyToClipboard
                data={offerLink || ''}
                isClickable={!isEmpty(offerLink)}
                successMessage={CopiedToClipboardSuccessMessage}
                failureMessage={CopiedToClipboardUnsuccessfulMessage}
              >
                <LinkIcon
                  className={
                    isEmpty(offerLink) ? classes.actionIconDisabled : ''
                  }
                  data-testid={`link-${offer.privateOfferId}`}
                  sx={{
                    fontSize: 'small',
                  }}
                />
              </CopyToClipboard>
            </LabraToolTip>
          </div>
        )}
        <div className={classes.actionIcon}>
          <Link
            to={{
              pathname: redirectURL,
              search,
            }}
            className={classes.contractLink}
            data-id="link-to-private-offer-details"
          >
            <LabraToolTip label={'View Offer details'} tooltipPlacement="top">
              <VisibilityOutlined
                fontSize="small"
                data-testid={`visibility-${offer.privateOfferId}`}
                className={classes.actionIcon}
              />
            </LabraToolTip>
          </Link>
        </div>
      </div>
    )
  }
)
export type PrivateOffersListWrapperProps = {
  crmOpportunityId?: string
  externalBaseURL?: string
  search?: string
}
const PrivateOffersWrapperUI: React.FC<PrivateOffersListWrapperProps> = ({
  externalBaseURL,
  search,
  crmOpportunityId,
}) => {
  const { classes } = useStyles()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { pageSize, privateOffers } = useSelector<PrivateOffersState>(
    state => state.privateOffers
  )
  //TO DO: https://ibexlabs.atlassian.net/browse/LT-3594 Remove this useEffect and its processing
  const { partner_id: accountIdFromParams } = useParams<{
    partner_id?: string
  }>()
  const accountId =
    useAccountId({ product: 'flyout' }) || accountIdFromParams || ''
  const isAdmin = useUserType() === PartnerType.Admin

  const getProductsAndClearOffers = useCallback(async () => {
    await dispatch(clearPrivateOffers())
    await dispatch(getProducts(accountId, selectedCloudTypeProductListing))
  }, [accountId, selectedCloudTypeProductListing])

  useEffect(() => {
    if (!isEmpty(accountId) && !externalBaseURL) {
      if (isEmpty(products)) {
        getProductsAndClearOffers()
      }
    }
  }, [accountId, selectedCloudTypeProductListing])
  useEffect(() => {
    if (!externalBaseURL && productId !== undefined && !isEmpty(products)) {
      {
        dispatch(
          getPrivateOffers({
            partnerId: accountId,
            cloudMarketplace: selectedCloudTypeProductListing,
            pageSize,
            pageNumber: 1,
            productId,
          })
        )
      }
    }
    return () => {
      dispatch(
        updateAppAlert({
          message: undefined,
          messageType: 'INFO',
          autoClose: true,
        })
      )
    }
  }, [accountId, productId, selectedCloudTypeProductListing, products])

  const handleInputChange = useCallback(
    (key: string, privateOfferId: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(updateOffer(key, event.target.value, privateOfferId))
      },
    []
  )
  const [selectedPrivateOffer, setSelectedPrivateOffer] = useState<{
    offer: DetailedOfferPreviewData
    planType: PlanType
  } | null>(null)
  const [openModal, setOpenModal] = useState<boolean>(false)

  const viewDetailedOffer = useCallback(
    (privateOffer: PrivateOffer) => () => {
      const planType =
        products.find(
          product => product.productId === privateOffer?.awsProductId
        )?.productType || PlanType.SAAS_CONTRACT
      const offer = {
        ...privateOffer,
        offerName: privateOffer.privateOfferName,
      } as DetailedOfferPreviewData
      setSelectedPrivateOffer({ offer, planType })
      setOpenModal(true)
    },
    [products]
  )
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  const updateOfferData = useCallback(
    (partnerId: string, privateOfferId: string) => () => {
      dispatch(patchOfferData(accountId, privateOfferId, crmOpportunityId))
    },
    []
  )

  const getOfferIdFieldForAdmin = (
    offer: PrivateOffer,
    cloud: CloudMarketplace
  ) => {
    switch (cloud) {
      case 'AWS': {
        return (
          <TextField
            onChange={handleInputChange('awsOfferId', offer.privateOfferId)}
            key={'awsOfferId'}
            id={`offer-id-${offer.privateOfferId}`}
            label={'Enter AWS offer ID'}
            placeholder="N/A"
            defaultValue={offer.awsOfferId}
            variant="standard"
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `offer-id-${offer.privateOfferId}`,
                'data-id': `offer-id-${offer.privateOfferId}`,
              },
            }}
          />
        ) as never
      }
      case 'AZURE': {
        return <></>
      }
      case 'GCP': {
        return <></>
      }
      case 'REDHAT': {
        return <></>
      }
    }
  }

  const getAdminObjectsForTable = () =>
    Object.values(privateOffers).map(offer => {
      const keyNameForCloudOfferId = getKeyCloudAccount(
        selectedCloudTypeProductListing
      )
      const valueForCloudOfferId = getOfferIdFieldForAdmin(
        offer,
        selectedCloudTypeProductListing
      )
      return {
        privateOfferName: offer.privateOfferName,
        id: offer.privateOfferId,
        companyName: offer.companyName,
        privateOfferId: offer.privateOfferId,
        crmOrderObjectId: offer.crmOrderObjectId,
        key: offer.privateOfferId,
        createdAt: (!isEmpty(offer.createdAt) ? (
          <DateTime date={offer.createdAt} />
        ) : (
          '-'
        )) as never,
        productName: (
          <ShortenedName name={offer.productName || ''} />
        ) as never,
        [keyNameForCloudOfferId]: valueForCloudOfferId,
        cloudOfferUrl: (
          <TextField
            onChange={handleInputChange('cloudOfferUrl', offer.privateOfferId)}
            id={`offer-url-${offer.privateOfferId}`}
            label={'Enter AWS offer URL'}
            placeholder="N/A"
            variant="standard"
            defaultValue={offer.cloudOfferUrl}
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `offer-url-${offer.privateOfferId}`,
                'data-id': `offer-url-${offer.privateOfferId}`,
              },
            }}
          />
        ) as never,
        actions: (
          <div className={classes.buttonContainer}>
            <ButtonV2
              onClick={updateOfferData(accountId, offer.privateOfferId)}
              className={classes.submitButton}
            >
              Submit
            </ButtonV2>
            <ButtonV2
              onClick={viewDetailedOffer(offer)}
              className={classes.submitButton}
            >
              Details
            </ButtonV2>
          </div>
        ) as never,
      }
    })

  const getObjectsForTable = () =>
    Object.values(privateOffers).map(offer => {
      const keyNameForCloudOfferId = getKeyCloudAccount(
        selectedCloudTypeProductListing
      )
      const valueForCloudOfferId = get(offer, `${keyNameForCloudOfferId}`, '')
      return {
        privateOfferId: (
          <ShortenedName name={offer.privateOfferId || ''} length={16} />
        ) as never,
        companyName: offer.companyName,
        key: offer.privateOfferId,
        productName: (
          <ShortenedName name={offer.productName || ''} length={21} />
        ) as never,
        offerStatus: getKeyFromValue(
          getStatusOffer(offer, selectedCloudTypeProductListing)
        )(offerStatusMap) as never,
        ...(selectedCloudTypeProductListing === 'AWS'
          ? { type: getOfferRenewalType(offer.type) }
          : {}),
        createdAt: (!isEmpty(offer.createdAt) ? (
          <DateTime date={offer.createdAt} />
        ) : (
          '-'
        )) as never,
        offerExpirationDate: getExpirationDateFromPrivateOffer(
          offer,
          selectedCloudTypeProductListing
        ),
        [keyNameForCloudOfferId]: (
          <ShortenedName name={valueForCloudOfferId || ''} length={21} />
        ) as never,
        actions: (
          <PrivateOffersActionsLabraUI
            externalBaseURL={externalBaseURL}
            search={search}
            classes={classes as ClassNameMap}
            offer={offer}
            productId={getProductIdFromPrivateOffer(
              offer,
              selectedCloudTypeProductListing
            )}
            cloudType={selectedCloudTypeProductListing}
          />
        ) as never,
      }
    })
  const privateOffersForTable = isAdmin
    ? getAdminObjectsForTable()
    : getObjectsForTable()

  const handleRoute = (selected: ColumnMenuOption) => {
    if (selected.key === 'offer' || selected.key === 'subscription') {
      navigate({
        pathname: selected.route,
        search: search,
      })
    } else if (selected?.route) {
      navigate(selected?.route)
    }
  }

  const newUIDisableBtn = (
    <div className={classes.toolTipView}>
      <LabraToolTipV2
        tooltipPlacement="bottom"
        description={'Please select the product to create private offer.'}
      >
        <ButtonV2
          variant="contained"
          styleType="primary"
          smallVariant
          disabled
          data-testid="create-private-offer"
        >
          Create
        </ButtonV2>
      </LabraToolTipV2>
    </div>
  )

  const createButtonWithSubscription = isEmpty(productId) ? (
    newUIDisableBtn
  ) : (
    <DropdownButtonV2
      variant="contained"
      styleType="primary"
      buttonText={'Create'}
      data-testid={'create-private-offer'}
      options={[
        {
          label: 'Create new private offer',
          route: `${
            externalBaseURL || '/flyout/products-listing'
          }/${productId}/private-offers/edit`,
          disable: false,
          key: 'offer',
        },
        {
          label: 'Create agreement based offer',
          route: `${
            externalBaseURL?.replace('/product-listing', '') || '/flyout'
          }/subscriptions/edit`,
          key: 'subscription',
          disable: selectedCloudTypeProductListing !== 'AWS',
          disabledTooltip: 'Coming soon',
        },
      ]}
      onOptionClick={handleRoute}
    />
  )

  return (
    <div
      className={clsx(
        isAdmin ? classes.adminContainer : classes.marginTopSpace
      )}
    >
      <div
        className={clsx(
          classes.createOfferBtn,
          classes.alignButton,
          classes.dropdownAndProduct
        )}
      >
        {!isAdmin ? (
          <>
            <div className={classes.dropDownWidth}>
              <ProductSelectorDropdown
                functionType={FlyoutFunction.PRIVATE_OFFERS}
                externalBaseURL={externalBaseURL}
                search={search}
              />
            </div>
            <div className={classes.verticalDivider}></div>
            {createButtonWithSubscription}
          </>
        ) : null}
      </div>

      <PrivateOffers
        crmOpportunityId={crmOpportunityId}
        privateOffersForTable={privateOffersForTable}
        selectedPrivateOffer={
          selectedPrivateOffer?.offer as unknown as DetailedOfferPreviewData
        }
        planType={selectedPrivateOffer?.planType as unknown as PlanType}
        openModal={openModal}
        handleModalClose={handleClose}
        DetailedOfferPreviewComponent={DetailedOffer}
      />
    </div>
  )
}

export default PrivateOffersWrapperUI
